import React, { useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  FormHelperText,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'

import { PrizeCategory } from 'consts/prize'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'

import { useStyles } from './styles'

interface PartnerIntegrationProps {
  storesEnabled: boolean
  setStoresEnabled: (value: boolean) => void
  tucanEnabled: boolean
  setTucanEnabled: (value: boolean) => void
  toteatEnabled: boolean
  setToteatEnabled: (value: boolean) => void
  category?: PrizeCategory
  setCategory: (category: PrizeCategory) => void
  currentSku: string
  setCurrentSku: (sku: string) => void
  skusList: string[]
  setSkusList: (skus: string[]) => void
  toteatBenefitId: string
  setToteatBenefitId: (id: string) => void
  errors?: {
    category?: {
      hasError: boolean
      message: string
    }
    skusList?: {
      hasError: boolean
      message: string
    }
    toteatBenefitId?: {
      hasError: boolean
      message: string
    }
  }
  isLoading?: boolean
  onValidationChange?: (hasErrors: boolean) => void
}

const categories = Object.values(PrizeCategory)

export const PartnerIntegration: React.FC<PartnerIntegrationProps> = ({
  storesEnabled,
  setStoresEnabled,
  tucanEnabled,
  setTucanEnabled,
  toteatEnabled,
  setToteatEnabled,
  category,
  setCategory,
  currentSku,
  setCurrentSku,
  skusList,
  setSkusList,
  toteatBenefitId,
  setToteatBenefitId,
  errors = {},
  isLoading = false,
  onValidationChange,
}) => {
  const classes = useStyles()

  // Validaciones según los requerimientos
  const isCategoryRequired = tucanEnabled || toteatEnabled
  const isSkuRequired = toteatEnabled && category === PrizeCategory.General
  const isToteatBenefitRequired = toteatEnabled && category === PrizeCategory.Checkout

  const categoryError =
    isCategoryRequired && !category
      ? { hasError: true, message: 'La categoría es obligatoria' }
      : errors?.category

  const skusError =
    isSkuRequired && skusList.length === 0
      ? { hasError: true, message: 'El SKU es requerido' }
      : errors?.skusList

  const benefitError =
    isToteatBenefitRequired && !toteatBenefitId.trim()
      ? { hasError: true, message: 'El ID de Beneficio Toteat es obligatorio' }
      : errors?.toteatBenefitId

  useEffect(() => {
    if (onValidationChange) {
      const hasErrors =
        (isCategoryRequired && !category) ||
        (isSkuRequired && skusList.length === 0) ||
        (isToteatBenefitRequired && !toteatBenefitId.trim())
      onValidationChange(hasErrors)
    }
  }, [
    category,
    skusList,
    toteatBenefitId,
    isCategoryRequired,
    isSkuRequired,
    isToteatBenefitRequired,
    onValidationChange,
  ])

  const handleCurrentSkuChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setCurrentSku(e.target.value)
  }

  const handleAddSku = () => {
    if (currentSku.trim()) {
      setSkusList([...skusList, currentSku.trim()])
      setCurrentSku('')
    }
  }

  const handleRemoveSku = (skuToRemove: string) => {
    setSkusList(skusList.filter(sku => sku !== skuToRemove))
  }

  const handleToteatBenefitIdChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setToteatBenefitId(e.target.value)
  }

  const handleCategoryChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setCategory(e.target.value as PrizeCategory)
  }

  return (
    <>
      <Grid item xs={11}>
        <Typography variant="h6">Integración con Partner</Typography>
      </Grid>

      <Grid item xs={6}>
        <CustomFormControlLabel
          control={
            <CustomCheckbox
              onChange={e => setStoresEnabled(e.target.checked)}
              checked={storesEnabled}
            />
          }
          label="Disponible en locales"
        />
      </Grid>

      <Grid item xs={6}>
        <FormControl className={classes.fullWidth} error={categoryError?.hasError}>
          <InputLabel>Categoría</InputLabel>
          <Select value={category} onChange={handleCategoryChange} required={isCategoryRequired}>
            {categories.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {categoryError?.hasError && <FormHelperText>{categoryError.message}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={6}>
        <CustomFormControlLabel
          control={
            <CustomCheckbox
              onChange={e => setTucanEnabled(e.target.checked)}
              checked={tucanEnabled}
            />
          }
          label="Disponible en Tucan"
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormControlLabel
          control={
            <CustomCheckbox
              onChange={e => setToteatEnabled(e.target.checked)}
              checked={toteatEnabled}
            />
          }
          label="Disponible en Toteat"
        />
      </Grid>

      {toteatEnabled && (
        <>
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={9}>
                <TextField
                  label="SKU"
                  value={currentSku}
                  fullWidth
                  required={isSkuRequired}
                  error={skusError?.hasError}
                  helperText={skusError?.message}
                  onChange={handleCurrentSkuChange}
                  disabled={isLoading}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddSku()
                    }
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <IconButton
                  size="medium"
                  onClick={handleAddSku}
                  disabled={!currentSku.trim() || isLoading}
                >
                  <AddIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="ID Beneficio Toteat"
              value={toteatBenefitId}
              fullWidth
              required={isToteatBenefitRequired}
              error={benefitError?.hasError}
              helperText={benefitError?.message}
              onChange={handleToteatBenefitIdChange}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={6}>
            <List>
              {skusList.map(sku => (
                <ListItem key={sku}>
                  <ListItemText primary={sku} />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleRemoveSku(sku)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        </>
      )}
    </>
  )
}

PartnerIntegration.defaultProps = {
  category: undefined,
  errors: {},
  isLoading: false,
  onValidationChange: undefined,
}

export default PartnerIntegration
